import clsx from 'clsx'
import styles from '../ControlSheetCells.module.scss'
import { BreakpointType, ControlSheetAnnotation, ControlSheetNote, ControlSheetRow } from '../../types/types'
import ArrowRight from '../../../../pages/ControlSheet/assets/arrow-right.svg'
import { TripTooltip } from '../../ui/TripTooltip'
import { TripAnnotationTooltip } from '../../ui/TripAnnotationTooltip'
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query'
import { ControlSheetDTO } from '@/types/controlsheet'
import ChatIcon from '../../assets/chat.svg'
import EditIcon from '../../assets/edit.svg'
import { DutyTooltip } from '../../ui/DutyTooltip'
import { MealReliefModal } from '../../ui/MealReliefModal'
import { DualTooltipMealRelief } from '../../ui/DualTooltipMealRelief'
import { Tooltip } from '@/ui/Tooltip'
import { AnnotationsPermission } from '@/pages/ControlSheet/ui/ControlSheetPage'
import { displayTime } from '../../helpers/displayTime'

interface Props {
    isOutbound: boolean
    index: number
    route: string
    row: ControlSheetRow
    value: BreakpointType
    mealRelief: number
    annotationsPermission: AnnotationsPermission
    refetchControlSheet: (
        options?: RefetchOptions | undefined
    ) => Promise<QueryObserverResult<ControlSheetDTO[], Error>>
    addTempNote: (note: ControlSheetNote) => void
    deleteTempNote: (noteId: string) => void
    addTempAnnotation: (annotation: ControlSheetAnnotation) => void
}

export const BreakpointCell = (props: Props) => {
    const {
        isOutbound,
        index,
        route,
        row,
        value,
        mealRelief,
        annotationsPermission,
        refetchControlSheet,
        addTempNote,
        deleteTempNote,
        addTempAnnotation,
    } = props

    if (isOutbound) {
        if (!row.outbound?.trip) {
            return <></>
        }
    } else {
        if (!row.inbound?.trip) {
            return <></>
        }
    }

    const trip = isOutbound ? row.outbound! : row.inbound!

    const breakpoints = trip?.breakpoints || []

    const breakpoint = breakpoints[index]

    if (breakpoint === 'SKIP_CELL') {
        return null
    }

    if (trip.isTakeoverRow) {
        return null
    }

    let bpType = (breakpoint && breakpoint.type) ?? 'normal'
    if (bpType === 'nearDrivingHours') {
        bpType = 'normal'
    }

    const text = (breakpoint && breakpoint.text) || ''

    // Find all normal type breakpoints in this row
    const normalBreakpoints = breakpoints
        .filter((bp) => bp !== 'SKIP_CELL')
        .map((bp, idx) => ({ ...bp, type: bp === null ? 'normal' : bp.type, index: idx }))
        .filter((bp) => (bp?.type ?? null) === 'normal' || (bp?.type ?? null) === 'nearDrivingHours')

    // Check if current cell is first or last normal cell
    const isFirstNormal = bpType === 'normal' && normalBreakpoints.length > 0 && normalBreakpoints[0].index === index

    // It's only last normal if it's NOT the last breakpoint (in which case Layover is the last normal)
    const isLastNormal =
        bpType === 'normal' &&
        normalBreakpoints.length > 0 &&
        index != breakpoints.length - 1 &&
        normalBreakpoints[normalBreakpoints.length - 1].index === index

    if (value === 'SKIP_CELL') {
        return null
    }

    const timingAnnotations = (trip?.annotations || [])
        // .filter((it) => it.annotationType === 'Timing')
        .filter((it) => value && value.time && it.identifier && it.identifier === `${index}-${value.time}`)
    const annotation = timingAnnotations.length > 0 ? timingAnnotations[0] : undefined

    const isMealRelief = bpType === 'mealRelief'
    const nextTripRoute = trip?.routeAfterBreak && trip?.routeAfterBreak !== route ? trip?.routeAfterBreak : undefined

    const clickTooltipComponent = isMealRelief ? (
        <MealReliefModal
            duty={trip?.duty ?? ''}
            trip={trip?.trip ?? ''}
            run={row.run}
            route={route}
            scheduled={value?.time ?? ''}
            index={index}
            reliefStartScheduled={trip?.breakStart ?? ''}
            nextTrip={trip?.tripAfterBreak ?? ''}
            nextTripScheduled={trip?.breakEnd ?? ''}
            nextTripRoute={nextTripRoute}
            minMealRelief={mealRelief}
            addTempNote={addTempNote}
            deleteTempNote={deleteTempNote}
            addTempAnnotation={addTempAnnotation}
            annotation={annotation}
        />
    ) : (
        <TripAnnotationTooltip
            breakpoint={breakpoint?.name ?? ''}
            duty={trip?.duty ?? ''}
            trip={trip?.trip ?? ''}
            run={row.run}
            route={route}
            scheduled={value?.time ?? ''}
            index={index}
            onUpdate={refetchControlSheet}
            addTempNote={addTempNote}
            deleteTempNote={deleteTempNote}
            addTempAnnotation={addTempAnnotation}
            // TODO: Fix this type
            contextType={'Departure'}
            annotation={annotation}
        />
    )

    let hoverTooltipComponent: JSX.Element

    if (isMealRelief) {
        hoverTooltipComponent = <DutyTooltip route={route} trip={trip} annotationsPermission={annotationsPermission} />
    } else if (breakpoint?.isArrow) {
        hoverTooltipComponent = (
            <TripTooltip
                tripNumber={trip.trip}
                isNearDrivingHours={trip.isNearDrivingHours}
                plannedTime={trip?.departStandTime ?? ''}
                departingDepot={trip.depotGarage ?? ''}
                notes={annotation?.notes ?? []}
                annotationsPermission={annotationsPermission}
            />
        )
    } else {
        hoverTooltipComponent = (
            <TripTooltip
                tripNumber={trip.trip}
                isNearDrivingHours={trip.isNearDrivingHours}
                plannedTime={value?.time ?? ''}
                notOperating={annotation?.serviceNotOperated}
                actualTime={annotation?.departureTime ?? undefined}
                notes={annotation?.notes ?? []}
                annotationsPermission={annotationsPermission}
                previous={
                    isFirstNormal && trip.previousTripForDuty
                        ? {
                              trip: trip.previousTripForDuty,
                              run: 'Unknown',
                              lateMealReliefMayAffect: trip.lateMealReliefMayAffect,
                          }
                        : undefined
                }
            />
        )
    }

    let time = <> {displayTime(value?.time)} </>
    if (annotation?.serviceNotOperated) {
        time = <div className={styles.notOperating}> Not operating </div>
    } else if (annotation?.departureTime) {
        time = (
            <div className={styles.changedTime}>
                <span className={styles.newTime}>{displayTime(annotation?.departureTime)}</span>
                <span className={styles.oldTime}>{displayTime(value?.time)}</span>
            </div>
        )
    }

    const mealReliefShiftEndChild = (
        <div
            className={clsx(styles.cell, {
                [styles.cellEdit]: bpType !== 'empty' && annotationsPermission === 'edit',
                [styles[`${bpType}Time`]]: bpType,
            })}
        >
            {text || time}

            {annotation && annotation.notes.length > 0 && (
                <div className={styles.commentIcon}>
                    <ChatIcon />
                </div>
            )}

            <div className={styles.editIcon}>
                <EditIcon />
            </div>
        </div>
    )

    if (bpType !== 'normal' && !breakpoint?.isArrow) {
        return (
            <DualTooltipMealRelief
                tooltip={bpType !== 'empty' ? hoverTooltipComponent : undefined}
                clickTooltip={clickTooltipComponent}
                trigger={annotationsPermission === 'edit' ? 'hover+click' : 'hover'}
                placement="bottom"
            >
                {mealReliefShiftEndChild}
            </DualTooltipMealRelief>
        )
    }

    const normalChild = (
        <div style={{ display: 'grid', alignContent: 'start' }}>
            <div
                className={clsx(styles.normalContainer, {
                    [styles.normalContainerLast]: isLastNormal,
                })}
            >
                <div
                    className={clsx(styles.cell, {
                        [styles.cellEdit]: annotationsPermission === 'edit' && !breakpoint?.isArrow,
                        [styles[`${bpType}Time`]]: bpType,
                        [styles.firstNormalTime]: isFirstNormal,
                        [styles.lastNormalTime]: isLastNormal,
                        [styles.arrow]: breakpoint?.isArrow,
                    })}
                >
                    <div
                        className={clsx({
                            [styles.nearDrivingHoursTime]: breakpoint && breakpoint.type === 'nearDrivingHours',
                        })}
                    >
                        {breakpoint?.isArrow ? <ArrowRight /> : text || time}
                    </div>

                    {annotation && annotation.notes.length > 0 && (
                        <div className={styles.commentIcon}>
                            <ChatIcon />
                        </div>
                    )}

                    <div className={styles.editIcon}>
                        <EditIcon />
                    </div>
                </div>
            </div>
        </div>
    )

    if (breakpoint?.isArrow) {
        return (
            <Tooltip tooltip={hoverTooltipComponent} placement="bottom" trigger="hover">
                {normalChild}
            </Tooltip>
        )
    }

    return (
        <DualTooltipMealRelief
            tooltip={hoverTooltipComponent}
            clickTooltip={clickTooltipComponent}
            placement="bottom"
            trigger={annotationsPermission === 'edit' ? 'hover+click' : 'hover'}
        >
            {normalChild}
        </DualTooltipMealRelief>
    )
}
